import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Button, Table } from 'reactstrap'
import { Modal } from '@byscott-io/corebyscott'
import { withUserStore } from '../../contexts/UserStore'
import { deepCamelizeKeys } from '../../modules/KeyTransform'
import Lex from '../Shared/Lex'

const ConnectedDevices = ({ userStore }) => {
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(true)
  const [revokeModal, setRevokeModal] = useState(false)
  const [bulkRevokeModal, setBulkRevokeModal] = useState(false)
  const [deviceToRevoke, setDeviceToRevoke] = useState(null)
  const [selectedDevices, setSelectedDevices] = useState(new Set())
  const [selectAll, setSelectAll] = useState(false)

  useEffect(() => {
    if (userStore.bearerToken) {
      loadDevices()
    }
  }, [userStore.bearerToken])

  const loadDevices = () => {
    setLoading(true)
    const { bearerToken } = userStore
    
    fetch('/api/v1/allowlisted_jwts', {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'JWT_AUD': 'WebApp',
        'Authorization': bearerToken
      }
    })
      .then(response => response.json())
      .then(data => {
        console.log('API response:', data) // Debug log
        const camelizedDevices = deepCamelizeKeys(data.allowlisted_jwts || [])
        console.log('Camelized devices:', camelizedDevices) // Debug log
        setDevices(camelizedDevices)
        setLoading(false)
      })
      .catch(error => {
        console.error('Error loading devices:', error)
        setLoading(false)
      })
  }

  const handleRevokeClick = (device) => {
    setDeviceToRevoke(device)
    setRevokeModal(true)
  }

  const handleRevokeConfirm = () => {
    if (deviceToRevoke) {
      const { bearerToken } = userStore
      
      fetch(`/api/v1/allowlisted_jwts/${deviceToRevoke.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'JWT_AUD': 'WebApp',
          'Authorization': bearerToken
        }
      })
        .then(response => response.json())
        .then(() => {
          setRevokeModal(false)
          setDeviceToRevoke(null)
          loadDevices()
        })
        .catch(error => {
          console.error('Error revoking device:', error)
        })
    }
  }

  const handleBulkRevoke = () => {
    if (selectedDevices.size === 0) return
    setBulkRevokeModal(true)
  }

  const handleBulkRevokeConfirm = () => {
    const { bearerToken } = userStore
    const deviceIds = Array.from(selectedDevices)
    
    Promise.all(deviceIds.map(deviceId =>
      fetch(`/api/v1/allowlisted_jwts/${deviceId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'JWT_AUD': 'WebApp',
          'Authorization': bearerToken
        }
      }).then(response => {
        if (!response.ok) {
          throw new Error(`Failed to revoke device ${deviceId}: ${response.status}`)
        }
        return response.json()
      })
    ))
      .then(() => {
        setBulkRevokeModal(false)
        setSelectedDevices(new Set())
        setSelectAll(false)
        loadDevices()
      })
      .catch(error => {
        console.error('Error bulk revoking devices:', error)
        setBulkRevokeModal(false) // Close modal even on error
        // Still refresh to show current state
        loadDevices()
      })
  }

  const handleSelectDevice = (deviceId) => {
    const newSelected = new Set(selectedDevices)
    if (newSelected.has(deviceId)) {
      newSelected.delete(deviceId)
    } else {
      newSelected.add(deviceId)
    }
    setSelectedDevices(newSelected)
    setSelectAll(newSelected.size === devices.filter(d => !isExpired(d.exp)).length)
  }

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedDevices(new Set())
      setSelectAll(false)
    } else {
      const activeDeviceIds = devices.filter(d => !isExpired(d.exp)).map(d => d.id)
      setSelectedDevices(new Set(activeDeviceIds))
      setSelectAll(true)
    }
  }


  const isExpired = (exp) => {
    // Handle both Unix timestamps and ISO date strings
    if (typeof exp === 'number') {
      return exp < Math.floor(Date.now() / 1000)
    }
    return new Date(exp) < new Date()
  }

  const getDeviceInfo = (aud, isOAuth = false) => {
    if (isOAuth) {
      return { name: 'OAuth API App', icon: '🔧' }
    }
    
    switch (aud) {
      case 'WebApp':
        return { name: 'Web Browser', icon: '🌐' }
      case 'MobileApp':
        return { name: 'Mobile App', icon: '📱' }
      case 'API':
        return { name: 'API Access', icon: '🔧' }
      default:
        return { name: aud || 'Unknown Device', icon: '💻' }
    }
  }

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return '📱'
      case 'tablet':
        return '💻'
      case 'desktop':
        return '🖥️'
      case 'smart tv':
      case 'tv':
        return '📺'
      case 'console':
        return '🎮'
      default:
        return '💻'
    }
  }

  if (loading) {
    return <div className="text-center p-4">Loading devices...</div>
  }

  return (
    <div className="connected-devices">
      <h3><Lex term="connected-devices" /></h3>
      <p className="text-muted mb-4">
        Manage your active sessions and connected applications. You can revoke access for any device or application at any time.
      </p>

      {devices.some(d => !isExpired(d.exp)) && (
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <div>
            <Button 
              size="sm" 
              color="outline-secondary" 
              onClick={handleSelectAll}
              className="me-2"
            >
              {selectAll ? 'Deselect All' : 'Select All'}
            </Button>
            {selectedDevices.size > 0 && (
              <span className="text-muted small">
                {selectedDevices.size} device{selectedDevices.size !== 1 ? 's' : ''} selected
              </span>
            )}
          </div>
          {selectedDevices.size > 0 && (
            <Button 
              size="sm" 
              color="danger" 
              onClick={handleBulkRevoke}
            >
              Revoke Selected ({selectedDevices.size})
            </Button>
          )}
        </div>
      )}

      {devices.length === 0 ? (
        <div className="text-center p-4">
          <p>No active sessions found.</p>
        </div>
      ) : (
        <Table responsive hover>
          <thead>
            <tr>
              <th width="40"></th>
              <th>Device/Application</th>
              <th>Browser & OS</th>
              <th>Location</th>
              <th>Last Used</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => {
              const isOAuth = device.oauthApplicationId != null
              const deviceInfo = getDeviceInfo(device.aud, isOAuth)
              const expired = isExpired(device.exp)
              
              return (
                <tr key={device.id}>
                  <td>
                    {!expired && (
                      <input
                        type="checkbox"
                        checked={selectedDevices.has(device.id)}
                        onChange={() => handleSelectDevice(device.id)}
                        className="form-check-input"
                      />
                    )}
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <span className="me-2">{getDeviceIcon(device.deviceType || deviceInfo.icon)}</span>
                      <div>
                        <div><strong>{device.deviceType || deviceInfo.name}</strong></div>
                        <small className="text-muted">
                          {isOAuth ? 'OAuth API Token' : device.aud}
                          {device.scopes && device.scopes.length > 0 && (
                            <span> • {device.scopes.join(', ')}</span>
                          )}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div>{device.browser || 'Unknown Browser'}</div>
                      <small className="text-muted">{device.os || 'Unknown OS'}</small>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div>{device.locationDisplay || 'Unknown Location'}</div>
                      <small className="text-muted">{device.ipAddress ? device.ipAddress.replace(/\.\d+$/, '.xxx') : ''}</small>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div>{device.lastUsedAt ? new Date(device.lastUsedAt).toLocaleDateString() : 'Unknown'}</div>
                      <small className="text-muted">
                        {device.lastUsedAt ? new Date(device.lastUsedAt).toLocaleTimeString() : ''}
                      </small>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${expired ? 'bg-secondary' : 'bg-success'}`}>
                      {expired ? 'Expired' : 'Active'}
                    </span>
                  </td>
                  <td>
                    {!expired && (
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleRevokeClick(device)}
                      >
                        Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}

      <div className="cbs-scope">
        <Modal
          open={revokeModal}
          onClose={() => setRevokeModal(false)}
          title="Revoke Device Access"
          size="md"
          footer={
            <>
              <Button color="secondary" onClick={() => setRevokeModal(false)}>Cancel</Button>
              <Button color="danger" onClick={handleRevokeConfirm}>Revoke Access</Button>
            </>
          }
        >
          Are you sure you want to revoke access for this device? This action cannot be undone and the device will need to sign in again.
        </Modal>

        <Modal
          open={bulkRevokeModal}
          onClose={() => setBulkRevokeModal(false)}
          title="Revoke Multiple Devices"
          size="md"
          footer={
            <>
              <Button color="secondary" onClick={() => setBulkRevokeModal(false)}>Cancel</Button>
              <Button color="danger" onClick={handleBulkRevokeConfirm}>
                Revoke {selectedDevices.size} Device{selectedDevices.size !== 1 ? 's' : ''}
              </Button>
            </>
          }
        >
          Are you sure you want to revoke access for <strong>{selectedDevices.size} selected device{selectedDevices.size !== 1 ? 's' : ''}</strong>?
          This action cannot be undone and these devices will need to sign in again.
        </Modal>
      </div>
    </div>
  )
}

ConnectedDevices.propTypes = {
  userStore: PropTypes.object.isRequired
}

export default withUserStore(ConnectedDevices)